import { request } from '@umijs/max';

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

interface PaymentHistory {
  id: number;
  amount: number;
  status: string;
  date: string;
  subscription: {
    id: number;
    name: string;
  } | null;
  invoice_id: string;
}

interface PaymentModelState {
  paymentMethods: PaymentMethod[];
  paymentHistory: PaymentHistory[];
  loading: boolean;
  historyLoading: boolean;
}

// API calls
const getPaymentMethods = async () => {
  const response = await request('/user/payment/methods', {
    method: 'GET',
  });
  return response;
};

const createSetupIntent = async () => {
  const response = await request('/user/payment/create-setup-intent', {
    method: 'POST',
  });
  return response;
};

const deletePaymentMethod = async (paymentMethodId: string) => {
  const response = await request(`/user/payment/methods/${paymentMethodId}`, {
    method: 'DELETE',
  });
  return response;
};

const setDefaultPaymentMethod = async (paymentMethodId: string) => {
  const response = await request(`/user/payment/methods/${paymentMethodId}/set-default`, {
    method: 'POST',
  });
  return response;
};

const getPaymentHistory = async () => {
  const response = await request('/user/payment/history', {
    method: 'GET',
  });
  return response;
};

export default {
  namespace: 'payment',
  
  state: {
    paymentMethods: [],
    paymentHistory: [],
    loading: false,
    historyLoading: false,
  } as PaymentModelState,

  effects: {
    *fetchPaymentMethods(_: any, { call, put }: any): Generator<any, any, any> {
      yield put({ type: 'setLoading', payload: true });
      
      try {
        const response = yield call(getPaymentMethods);
        
        yield put({
          type: 'savePaymentMethods',
          payload: response.paymentMethods || [],
        });
        
        return response;
      } catch (error) {
        console.error('Failed to fetch payment methods:', error);
        throw error;
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },
    
    *createSetupIntent(_: any, { call }: any): Generator<any, any, any> {
      try {
        const response = yield call(createSetupIntent);
        return response;
      } catch (error) {
        console.error('Failed to create setup intent:', error);
        throw error;
      }
    },
    
    *deletePaymentMethod({ payload }: { payload: string }, { call, put }: any): Generator<any, any, any> {
      try {
        const response = yield call(deletePaymentMethod, payload);
        // Refresh the payment methods after deletion
        yield put({ type: 'fetchPaymentMethods' });
        return response;
      } catch (error) {
        console.error('Failed to delete payment method:', error);
        throw error;
      }
    },
    
    *setDefaultPaymentMethod({ payload }: { payload: string }, { call, put }: any): Generator<any, any, any> {
      try {
        const response = yield call(setDefaultPaymentMethod, payload);
        // Refresh the payment methods after setting default
        yield put({ type: 'fetchPaymentMethods' });
        return response;
      } catch (error) {
        console.error('Failed to set default payment method:', error);
        throw error;
      }
    },
    
    *fetchPaymentHistory(_: any, { call, put }: any): Generator<any, any, any> {
      yield put({ type: 'setHistoryLoading', payload: true });
      
      try {
        const response = yield call(getPaymentHistory);
        
        yield put({
          type: 'savePaymentHistory',
          payload: response.payments || [],
        });
        
        return response;
      } catch (error) {
        console.error('Failed to fetch payment history:', error);
        throw error;
      } finally {
        yield put({ type: 'setHistoryLoading', payload: false });
      }
    },
  },

  reducers: {
    savePaymentMethods(state: PaymentModelState, { payload }: { payload: PaymentMethod[] }) {
      return {
        ...state,
        paymentMethods: payload,
      };
    },
    
    setLoading(state: PaymentModelState, { payload }: { payload: boolean }) {
      return {
        ...state,
        loading: payload,
      };
    },
    
    savePaymentHistory(state: PaymentModelState, { payload }: { payload: PaymentHistory[] }) {
      return {
        ...state,
        paymentHistory: payload,
      };
    },
    
    setHistoryLoading(state: PaymentModelState, { payload }: { payload: boolean }) {
      return {
        ...state,
        historyLoading: payload,
      };
    },
  },
}; 